import type { Call, Device } from "@twilio/voice-sdk";
import { EVENTS } from "~/modules/telemetry/Analytics";
import type { VoiceCall } from "~/modules/VoiceCall";
import { VoiceCallImpl } from "~/modules/VoiceCall";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { getLogger } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { VoiceControllerImpl } from "~/modules/voice/VoiceControllerImpl";
import { Action } from "~/modules/actions/Action";
import {
    addCallContextParameter,
    checkWorker,
    isSupervisorAlreadyMonitoringCallTask
} from "~/modules/actions/ActionUtils";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { CbmImpl } from "~/modules/cbm/CbmImpl";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";




export interface MonitorCallOptions {
    extraParams?: Record<any, any>;
    voiceDevice?: Device;
}






























export class MonitorCall implements Action<Promise<VoiceCall>> {
    


    readonly #taskSid: string;

    


    readonly #reservationSid: string;

    


    readonly #options?: MonitorCallOptions;

    constructor(taskSid: string, reservationSid: string, options?: MonitorCallOptions) {
        this.#taskSid = taskSid;
        this.#reservationSid = reservationSid;
        this.#options = options;
    }

    async run(ctx: {}): Promise<VoiceCall> {
        const context = ctx as ContextManager;

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();

        const session = context.retrieveInstanceOf(ContextInstance.SessionImpl, SessionImpl);
        const flexSessionIdentifier = session.flexSessionIdentifier;

        const voiceController = context.retrieveInstanceOf(ContextInstance.VoiceControllerImpl, VoiceControllerImpl);
        const logger = getLogger(context)("MonitorCall");
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);

        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        logger.debug(`monitorCall invoked with taskSid: ${this.#taskSid}`);

        const worker = taskRouter.worker;
        const task = await taskRouter.workspace.fetchTask(this.#taskSid);

        checkWorker(worker, {
            actionName: "MonitorCall",
            moduleName: "actions/Voice",
            analytics,
            context,
            worker,
            logger,
            event: EVENTS.MonitorCallCompleted,
            extra: {
                taskSid: this.#taskSid,
                reservationSid: this.#reservationSid,
                customVoiceDevice: !!this.#options?.voiceDevice
            }
        });

        if (await isSupervisorAlreadyMonitoringCallTask(task, worker, cbm)) {
            const errorMsg = `MonitorCall: Supervisor is already monitoring call for task ${this.#taskSid}`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.MonitorCallCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                reservationSid: this.#reservationSid
            });
            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.SupervisorAlreadyMonitoringCall,
                    { severity: ErrorSeverity.Error },
                    errorMsg
                )
            );
        }

        const effectiveExtraParams = {
            ...this.#options?.extraParams,
            To: addCallContextParameter(
                `flex_session_identifier=${flexSessionIdentifier}`,
                taskRouter.worker.attributes.contact_uri as string,
                this.#options?.extraParams?.To as string | undefined
            )
        };

        const voiceDevice = this.#options?.voiceDevice;
        await voiceController.init(voiceDevice);

        return new Promise((resolve, reject) => {
            const handleIncomingCall = (call: Call) => {
                logger.info("Incoming call event received in monitorCall");

                const voiceCall = new VoiceCallImpl(context, call, voiceController.voiceDevice);

                sendWorkerTrackingEvent(EVENTS.MonitorCallCompleted, analytics, worker, {
                    taskSid: this.#taskSid,
                    reservationSid: this.#reservationSid,
                    callSid: voiceCall.call?.parameters?.CallSid
                });

                voiceController.unsubscribeFromIncomingCallEvent(handleIncomingCall);

                resolve(voiceCall);
            };
            voiceController.subscribeToIncomingCallEvent(handleIncomingCall);
            
            taskRouter.worker.monitor(this.#taskSid, this.#reservationSid, effectiveExtraParams).catch((error) => {
                const errorMsg = `MonitorCall: Failed to monitor call for task ${this.#taskSid}`;
                logger.error(errorMsg, error);
                sendWorkerTrackingEvent(EVENTS.MonitorCallCompleted, analytics, worker, {
                    error: `${errorMsg} (${toCauseMessage(error)})`,
                    taskSid: this.#taskSid,
                    reservationSid: this.#reservationSid
                });
                return reject(
                    createFlexSdkError(context)(
                        ErrorCode.FailedToMonitorCall,
                        { severity: ErrorSeverity.Error },
                        errorMsg,
                        error as Error
                    )
                );
            });
        });
    }
}
